/*
  <product-card>: color-aware card images (snippets/product-card.liquid).
  Swatches switch the card's color in place by fetching the product-card-media section
  for that variant. Custom element, so cards re-initialise after facets.js swaps the grid.
*/
if (!customElements.get('product-card')) {
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mediaCache = new Map();
  // Once per page load; filtering/sorting re-renders the grid without reloading the page.
  let nudgePlayed = false;

  const fetchCardMedia = (variantUrl) => {
    if (!mediaCache.has(variantUrl)) {
      const url = `${variantUrl}${variantUrl.includes('?') ? '&' : '?'}section_id=product-card-media`;
      const request = fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(`Card media request failed: ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const track = doc.querySelector('[data-card-media-track]');
          if (!track) throw new Error('Card media response had no slides');
          const progress = doc.querySelector('.card-media__progress');
          return track.outerHTML + (progress ? progress.outerHTML : '');
        })
        .catch((error) => {
          mediaCache.delete(variantUrl);
          throw error;
        });
      mediaCache.set(variantUrl, request);
    }
    return mediaCache.get(variantUrl);
  };

  class ProductCard extends HTMLElement {
    connectedCallback() {
      this.media = this.querySelector('.card-media .media');
      if (!this.media) return;

      if (!this.initialized) {
        this.initialized = true;
        this.links = this.querySelectorAll('[data-card-link]');
        this.swatches = [...this.querySelectorAll('.color-swatch[data-variant-url]')];
        this.swatches.forEach((swatch) => {
          swatch.addEventListener('click', () => this.selectSwatch(swatch));
          // Images only change on click; hover/focus just warms the request so the click feels instant.
          ['pointerenter', 'pointerdown', 'focus'].forEach((type) => {
            swatch.addEventListener(type, () => this.prefetch(swatch));
          });
        });
      }

      this.bindProgress();
      if (this.hasAttribute('data-nudge')) this.observeNudge();
    }

    disconnectedCallback() {
      this.nudgeObserver?.disconnect();
    }

    get track() {
      return this.media.querySelector('[data-card-media-track]');
    }

    get leadImage() {
      return this.media.querySelector('.card-media__slide img');
    }

    prefetch(swatch) {
      if (swatch.getAttribute('aria-pressed') === 'true') return;
      fetchCardMedia(swatch.dataset.variantUrl).catch(() => {});
    }

    showPreviewImage(swatch) {
      const img = this.leadImage;
      if (!img || !swatch.dataset.previewSrc) return;
      img.srcset = swatch.dataset.previewSrcset;
      img.src = swatch.dataset.previewSrc;
    }

    async selectSwatch(swatch) {
      if (swatch.getAttribute('aria-pressed') === 'true') return;

      // Commit right away so the swatch, links and lead image respond before the slides arrive.
      this.showPreviewImage(swatch);
      if (this.track) this.track.scrollLeft = 0;
      this.swatches.forEach((item) => item.setAttribute('aria-pressed', String(item === swatch)));
      this.links.forEach((link) => {
        link.href = swatch.dataset.cardUrl;
      });

      const request = fetchCardMedia(swatch.dataset.variantUrl);
      this.pendingRequest = request;
      try {
        const html = await request;
        if (this.pendingRequest !== request) return;
        this.media.innerHTML = html;
        this.bindProgress();
      } catch (error) {
        if (this.pendingRequest === request) window.location.assign(swatch.dataset.variantUrl);
      }
    }

    // Slide the thumb with the swipe: scrollLeft / slide width is the fractional slide index.
    bindProgress() {
      const track = this.track;
      const thumb = this.media.querySelector('.card-media__progress-thumb');
      if (!track || !thumb) return;

      let frame = 0;
      track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => {
            thumb.style.transform = `translateX(${(track.scrollLeft / track.clientWidth) * 100}%)`;
          });
        },
        { passive: true }
      );
    }

    // First card on touch devices peeks at its next photo, once per page load.
    observeNudge() {
      if (nudgePlayed || canHover.matches || reducedMotion.matches) return;
      const track = this.track;
      if (!track || track.children.length < 2) return;

      this.nudgeObserver?.disconnect();
      this.nudgeObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting || nudgePlayed) return;
          nudgePlayed = true;
          this.nudgeObserver.disconnect();
          setTimeout(() => this.nudge(track), 600);
        },
        { threshold: 0.75 }
      );
      this.nudgeObserver.observe(this.media);
    }

    nudge(track) {
      // Snap would pull a partial scroll straight back, so pause it for the peek.
      track.style.scrollSnapType = 'none';
      track.scrollTo({ left: track.clientWidth * 0.25, behavior: 'smooth' });
      setTimeout(() => {
        track.scrollTo({ left: 0, behavior: 'smooth' });
        setTimeout(() => {
          track.style.scrollSnapType = '';
        }, 500);
      }, 500);
    }
  }

  customElements.define('product-card', ProductCard);
}
